import type { BaseSyntheticEvent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect } from 'react';
import type { RezaOpravneniDto } from '@gov-nx/api';
import type { RezaParamsMutable } from '@gov-nx/core/hooks';
import { useRezaEligibleList } from '@gov-nx/core/hooks';
import type { FormDefinition2 } from '@gov-nx/core/service';
import type { FilterParam } from '@gov-nx/core/types';
import { compareProperty } from '@gov-nx/core/types';
import type { IUseBoolean } from '@gov-nx/utils/common';
import { useBoolean } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import type { ComplaintsForm } from '../useComplaintsForm';
import { useComplaintsForm } from '../useComplaintsForm';
import { useFilters } from './useFilters';
import type { RezaFilterForm, RezaFormFields } from './useForm';
import { useForm } from './useForm';

interface RezaListContexts {
	code: PageCode;
	isLoading: boolean;
	isError: boolean;
	data: RezaOpravneniDto[];
	formDefinition: FormDefinition2<RezaFilterForm, RezaFormFields>;
	onSubmit: (event: BaseSyntheticEvent) => void;
	onFilterReset: () => void;
	filterParams: FilterParam[];
	isFilterModalOpen: IUseBoolean;
	isRezaOverviewModalOpen: IUseBoolean;
	isRezaUsageModalOpen: IUseBoolean;
	isFilterModalActive: boolean;
	complaintsForm: ComplaintsForm;
}

const Context = createContext<RezaListContexts | null>(null);

export const RezaListContextProvider = ({
	children,
	params,
	code,
	isArchivePage,
}: PropsWithChildren<{
	code: PageCode;
	isArchivePage: boolean;
	params: RezaParamsMutable;
}>) => {
	const isFilterModalOpen = useBoolean();
	const isRezaOverviewModalOpen = useBoolean();
	const isRezaUsageModalOpen = useBoolean();

	const complaintsForm = useComplaintsForm();

	const { filterParams, filter, updateFilters, reset, isFilterModalActive } =
		useFilters();

	const formDefinition = useForm({ filter, params });

	const query = useRezaEligibleList({ filter, params, isArchivePage });

	useEffect(() => {
		const subscription = formDefinition.formMethods.watch((value, { name }) => {
			if (name === 'role' && value['role']) {
				params.update({
					role: value['role'].zmocnenec ? 'zmocnenec' : 'zmocnitel',
				});
			}
		});

		return () => subscription.unsubscribe();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [formDefinition.formMethods]);

	const onSubmit = formDefinition.formMethods.handleSubmit(() => {
		updateFilters(formDefinition.formMethods.getValues());
		isFilterModalOpen.setFalse();
	});

	const onFilterReset = () => {
		formDefinition.formReset();
		reset();
		isFilterModalOpen.setFalse();
	};

	return (
		<Context.Provider
			value={{
				code,
				isLoading: query.isLoading,
				isError: query.isError,
				data: query.data?.seznam?.sort(compareProperty('nazevSablony')) ?? [],
				formDefinition,
				onSubmit,
				onFilterReset,
				filterParams: filterParams(formDefinition),
				isFilterModalOpen,
				isRezaOverviewModalOpen,
				isRezaUsageModalOpen,
				isFilterModalActive,
				complaintsForm,
			}}>
			{children}
		</Context.Provider>
	);
};

export const useRezaListContext = (): RezaListContexts => {
	const context = useContext(Context);
	if (!context) {
		throw new Error(
			'useRezaListContext must be used within a RezaListContextProvider'
		);
	}
	return context;
};
